package vegasful.admin.loaders

import androidx.compose.runtime.*
import com.steamstreet.graphkt.asOptionalInput
import kotlinx.datetime.TimeZone
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.jsonPrimitive
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import vegasful.admin.Application
import vegasful.admin.admin
import vegasful.admin.api.LoaderConfiguration
import vegasful.admin.api.LoaderExecutionInput
import vegasful.admin.api.client.AdminLoader
import vegasful.admin.api.client.AdminLoaderModule
import vegasful.admin.api.client._AdminLoaderQuery
import vegasful.admin.components.*
import vegasful.admin.views.contentContainer

/**
 * Tolerant parser for loader event data. The cached events may carry fields the admin's
 * [LoaderEvent] model doesn't (yet) declare; ignore those rather than failing the whole parse.
 */
internal val lenientJson = Json { ignoreUnknownKeys = true }

fun _AdminLoaderQuery.default() {
    id
    name
    schedule
    parameters
    module {
        id
        this.name
    }
}

@Composable
fun loader(loaderId: String?) {
    val scope = rememberCoroutineScope()
    var modules by remember { mutableStateOf<List<AdminLoaderModule>?>(null) }
    var loader by remember { mutableStateOf<AdminLoader?>(null) }
    var executeData by remember { mutableStateOf<String?>(null) }
    var executing by remember { mutableStateOf(false) }
    var lastLoaded by remember { mutableStateOf<List<LoaderEvent>?>(null) }

    /**
     * Run the loader with optional input.
     */
    suspend fun execute(data: String?) {
        if (loaderId == null) {
            return
        }
        executing = true
        try {
            Application.api.admin {
                loaders {
                    loader(loaderId) {
                        execute(LoaderExecutionInput(data).asOptionalInput())
                    }
                }
            }.loaders.loader.execute
        } catch (e: Throwable) {
            Application.notifications.showError("Loader execution failed: ${e.message}")
        } finally {
            executing = false
        }
    }

    /**
     * Run the loader with optional input.
     */
    suspend fun reprocess() {
        if (loaderId == null) {
            return
        }
        executing = true
        try {
            Application.api.admin {
                loaders {
                    loader(loaderId) {
                        reprocess
                    }
                }
            }.loaders.loader.reprocess
        } catch (e: Throwable) {
            Application.notifications.showError("Loader execution failed: ${e.message}")
        } finally {
            executing = false
        }
    }

    LaunchedEffect(loaderId) {
        Application.api.admin {
            loaders {
                modules {
                    this.id
                    this.name
                }
                if (loaderId != null) {
                    loader(loaderId) {
                        default()
                        lastLoad {
                            ts
                            events
                        }
                    }
                }
            }
        }.loaders.let {
            if (loaderId != null) {
                loader = it.loader

                try {
                    it.loader.lastLoad?.let {
                        lastLoaded = lenientJson.decodeFromString<List<LoaderEvent>?>(it.events)?.sortedBy {
                            it.id
                        }
                    }
                } catch (t: Throwable) {
                    // Don't silently blank the editor — surface why the load data failed to parse.
                    console.error("Failed to parse loader last-load events", t)
                }
            }
            modules = it.modules.sortedBy { it.name.lowercase() }
        }
    }

    contentContainer {
        entityView {
            title = if (loaderId == null) "New Loader" else loader?.name
            breadcrumbs {
                crumb("Loaders", "content/loaders")
            }

            if (loaderId != null) {
                action {
                    title = "Delete"
                    action {
                        Application.api.admin {
                            loaders {
                                this.deregister(loaderId)
                            }
                        }.loaders.deregister.let {
                            Application.navigation.navigate("content/loaders")
                        }
                    }
                }

                action {
                    title = "Execute"
                    primary = true
                    showProgressOnAction = true
                    action {
                        execute(null)
                    }
                }
                action {
                    title = "Reprocess"
                    primary = true
                    showProgressOnAction = true
                    action {
                        reprocess()
                    }
                }
            }

            content {
                box({

                }) {
                    var newId by remember { mutableStateOf<String?>(null) }
                    var update by remember { mutableStateOf(LoaderConfiguration(name = loader?.name.orEmpty())) }

                    dialogField("ID") {
                        Input(InputType.Text) {
                            if (loaderId != null) {
                                disabled()
                            }
                            value((loaderId ?: newId).orEmpty())
                            onInput {
                                newId = it.value.ifBlank { null }
                            }
                        }
                    }

                    dialogField("Name") {
                        simpleTextField(update.name) {
                            update = update.copy(name = it.orEmpty())
                        }
                    }

                    dialogField("Schedule") {
                        cronScheduleEditor(
                            (update.schedule ?: loader?.schedule)?.ifBlank { null },
                            TimeZone.currentSystemDefault()
                        ) { newSchedule ->
                            val updated = newSchedule ?: ""
                            update = update.copy(schedule = updated.takeIf { it != loader?.schedule })
                        }
                    }

                    dialogField("Module") {
                        Select({
                            onChange {
                                update = update.copy(module = if (it.value == "none") "" else it.value)
                            }
                        }) {
                            Option("none", {
                                if (loader?.module == null) selected()
                            }) {
                                Text("No loader module")
                            }
                            modules?.forEach { module ->
                                Option(module.id, {
                                    if (module.id == loader?.module?.id) selected()
                                }) {
                                    Text(module.name)
                                }
                            }
                        }
                    }

                    if (loader?.module?.id != "ManualLoader") {
                        // Allow Empty Results checkbox
                        val currentParams = update.parameters ?: loader?.parameters
                        val allowEmpty = parseParametersFromJson(currentParams)["allowEmpty"] == "true"

                        dialogField("Allow Empty Results") {
                            simpleCheckBox(
                                "Don't report an error when this loader returns no events (useful for seasonal venues)",
                                allowEmpty
                            ) { checked ->
                                val params = parseParametersFromJson(currentParams).toMutableMap()
                                if (checked) {
                                    params["allowEmpty"] = "true"
                                } else {
                                    params.remove("allowEmpty")
                                }
                                val newJson = if (params.isEmpty()) null else convertParametersToJson(params)
                                update = update.copy(parameters = newJson)
                            }
                        }

                        dialogField("Parameters") {
                            keyValueEditor(update.parameters ?: loader?.parameters) { newParameters ->
                                update = update.copy(
                                    parameters = newParameters?.takeIf { it != loader?.parameters }
                                )
                            }
                        }
                    }

                    Div {
                        button("Save", true, true) {
                            if (loaderId == null) {
                                if (!newId.isNullOrBlank()) {
                                    try {
                                        Application.api.admin {
                                            loaders {
                                                registerLoader(newId!!, update) {
                                                    default()
                                                }
                                            }
                                        }.loaders.registerLoader.let {
                                            Application.navigation.navigate("content/loaders/${it.id}")
                                        }
                                    } catch (e: Throwable) {
                                        Application.notifications.showError("The loader wasn't created")
                                    }
                                } else {
                                    Application.notifications.showError("You must enter a valid id")
                                }
                            } else {
                                Application.api.admin {
                                    loaders {
                                        this.loader(loaderId) {
                                            this.update(update) {
                                                default()
                                            }
                                        }
                                    }
                                }.loaders.loader.update.let {
                                    Application.navigation.navigate("content/loaders/${it.id}")
                                }
                            }
                        }

                        if (loaderId != null) {
                            button("Revert") {
                                update = LoaderConfiguration(name = loader?.name.orEmpty())
                            }
                        }
                    }
                }

                if (loaderId != null) {
                    val loaderModule = loader?.module
                    if (loaderModule?.id == "ManualLoader") {
                        loaderDataEditor(loaderId, lastLoaded)
                    } else {
                        if (lastLoaded != null) {
                            box("Last Load", {}) {
                                eventsViewer(lastLoaded.orEmpty())
                            }
                        }
                    }
                }
            }
        }
    }
}

/**
 * Helper function to parse JSON parameters into key-value pairs
 */
private fun parseParametersFromJson(json: String?): Map<String, String> {
    if (json.isNullOrBlank()) return emptyMap()

    return try {
        val jsonObject = Json.parseToJsonElement(json) as? JsonObject
        jsonObject?.mapValues { it.value.jsonPrimitive.content } ?: emptyMap()
    } catch (e: Exception) {
        emptyMap()
    }
}

/**
 * Helper function to convert key-value pairs to JSON string
 */
private fun convertParametersToJson(parameters: Map<String, String>): String {
    if (parameters.isEmpty()) return "{}"

    val jsonObject = JsonObject(parameters.mapValues { JsonPrimitive(it.value) })
    return Json.encodeToString(JsonObject.serializer(), jsonObject)
}

/**
 * A component for editing key-value pairs for loader parameters
 */
@Composable
fun keyValueEditor(
    initialParameters: String?,
    onParametersChange: (String?) -> Unit
) {
    var parameters by remember { mutableStateOf(parseParametersFromJson(initialParameters)) }
    var newKey by remember { mutableStateOf("") }
    var newValue by remember { mutableStateOf("") }
    var addingNew by remember { mutableStateOf(false) }

    // Update parameters when they change
    LaunchedEffect(parameters) {
        val jsonString = if (parameters.isEmpty()) null else convertParametersToJson(parameters)
        onParametersChange(jsonString)
    }

    // Update if initial parameters change
    LaunchedEffect(initialParameters) {
        parameters = parseParametersFromJson(initialParameters)
    }

    if (parameters.isEmpty() && !addingNew) {
        Div({
            style {
                padding(1.cssRem)
            }
        }) {
            Text("No parameters configured.")
        }
    } else {
        // Display existing parameters
        parameters.forEach { (key, value) ->
            Div({
                style {
                    padding(0.5.cssRem)
                    display(DisplayStyle.Flex)
                    gap(0.5.cssRem)
                }
            }) {
                Div({
                    style {
                        flexGrow(1)
                        display(DisplayStyle.Flex)
                        gap(0.5.cssRem)
                    }
                }) {
                    Input(InputType.Text) {
                        value(key)
                        style {
                            flexGrow(1)
                        }
                        onInput { newKey ->
                            val newParams = parameters.toMutableMap()
                            newParams.remove(key)
                            newParams[newKey.value] = value
                            parameters = newParams
                        }
                    }
                    Input(InputType.Text) {
                        value(value)
                        style {
                            flexGrow(2)
                        }
                        onInput { newValue ->
                            parameters = parameters.toMutableMap().apply {
                                put(key, newValue.value)
                            }
                        }
                    }
                }
                icon("delete") {
                    action {
                        parameters = parameters.toMutableMap().apply {
                            remove(key)
                        }
                    }
                }
            }
        }

        // Add new parameter form
        if (addingNew) {
            Div({
                style {
                    padding(0.5.cssRem)
                    display(DisplayStyle.Flex)
                    gap(0.5.cssRem)
                }
            }) {
                Div({
                    style {
                        flexGrow(1)
                        display(DisplayStyle.Flex)
                        gap(0.5.cssRem)
                    }
                }) {
                    Input(InputType.Text) {
                        value(newKey)
                        style {
                            flexGrow(1)
                        }
                        onInput {
                            newKey = it.value
                        }
                    }
                    Input(InputType.Text) {
                        value(newValue)
                        style {
                            flexGrow(2)
                        }
                        onInput {
                            newValue = it.value
                        }
                    }
                }
                icon("check") {
                    action {
                        if (newKey.isNotBlank()) {
                            parameters = parameters.toMutableMap().apply {
                                put(newKey, newValue)
                            }
                            newKey = ""
                            newValue = ""
                            addingNew = false
                        }
                    }
                }
                icon("close") {
                    action {
                        newKey = ""
                        newValue = ""
                        addingNew = false
                    }
                }
            }
        }
    }

    // Add button
    if (!addingNew) {
        Div({
            style {
                padding(0.5.cssRem)
            }
        }) {
            button("Add Parameter") {
                addingNew = true
            }
        }
    }
}

